import { selectScanColoring } from "@/data-preparation-tool/store/data-preparation-view-options/data-preparation-view-options-selectors";
import { ScanColoring } from "@/data-preparation-tool/store/data-preparation-view-options/data-preparation-view-options-slice";
import { colorForIndex } from "@/registration-tools/common/rendering/use-point-cloud-materials";
import { curryAppSelector } from "@/store/reselect";
import { RootState } from "@/store/store";
import {
  blue,
  ColorString,
  dataComparisonColorsDark,
  dataComparisonColorsSorted,
  dataComparisonColorsSortedDark,
} from "@faro-lotv/flat-ui";
import { EMPTY_ARRAY, GUID } from "@faro-lotv/foundation";
import { isIElementGenericPointCloudStream } from "@faro-lotv/ielement-types";
import { selectLoadedIElements } from "@faro-lotv/project-source";
import {
  CaptureTreeEntityRevision,
  isRevisionScanEntity,
  RegistrationEdgeRevision,
  RevisionScanEntity,
} from "@faro-lotv/service-wires";
import { createSelector } from "@reduxjs/toolkit";
import { Vector3 } from "three";
import { selectRevisionAtomicClusterIds } from "../revision/revision-cluster-selectors";
import {
  selectGlobalEdges,
  selectPartOfEdges,
  selectRevisionRegistrationEdges,
} from "../revision/revision-edge-selectors";
import {
  selectRevisionEntities,
  selectRevisionEntityChildrenMap,
  selectRevisionEntityMap,
  selectRootEntity,
} from "../revision/revision-entity-selectors";
import {
  selectRevisionEntityDescendantScans,
  selectRevisionScans,
} from "../revision/revision-scan-selectors";
import {
  getAncestorIds,
  getDescendantIds,
  getEntityPosition,
  getEntityTransformCache,
  getPointCloudStreamForScanEntity,
  getPointCloudStreamIdsForEntities,
  isEntityDeletedOnRevision,
} from "../revision/selector-helpers";
import { findConnectedComponents } from "../selector-helpers";
import { selectDataPreparationTransformCache } from "../user-edits/data-preparation-user-edit-selectors";
import { VisibilityMap } from "./data-preparation-ui-slice";
import {
  computeActionableCaptures,
  computeActionableClusters,
  computeAffectedAncestorIds,
  computeRecursiveEntities,
} from "./selector-helpers";

/**
 * @returns the selected entities' id in the scan tree
 * @param state current app state
 */
export function selectSelectedEntityIds(state: RootState): GUID[] {
  return state.dataPreparationUi.selectedEntityIds;
}

/**
 * @returns the IDs of the entities that user edits should be applied to.
 *  In particular, this excludes clusters if some of their children are selected individually.
 */
export const selectEntitiesToEdit = createSelector(
  [
    selectSelectedEntityIds,
    selectRevisionEntityChildrenMap,
    selectRevisionEntityMap,
    selectPartOfEdges,
  ],
  computeAffectedAncestorIds,
);

/**
 * @param state current application state
 * @param entityIds the entity ids to compute the affected entities for
 * @returns the IDs of the entities that are impacted directly or indirectly with the current entities in the array.
 */
export const selectAffectedAncestorIds = curryAppSelector(
  createSelector(
    [
      (state: RootState, entityIds: GUID[]) => entityIds,
      selectRevisionEntityChildrenMap,
      selectRevisionEntityMap,
      selectPartOfEdges,
    ],
    computeAffectedAncestorIds,
  ),
);

/**
 * @param state current app state
 * @returns The id of selected connection and its corresponding scans.
 */
export function selectSelectedConnectionId(state: RootState): GUID | undefined {
  return state.dataPreparationUi.selectedConnectionId;
}

/**
 * @returns The registration revision of the hovered connection.
 */
export const selectSelectedConnectionDetails = createSelector(
  [selectSelectedConnectionId, selectRevisionRegistrationEdges],
  (selectedConnectionId, edges): RegistrationEdgeRevision | undefined =>
    edges.find((edge) => edge.id === selectedConnectionId),
);

/**
 * @param state current app state
 * @param id The ID of the connection to check whether it is selected.
 * @returns true if given id is same as selected connection id
 */
export const selectIsConnectionSelected = createSelector(
  [selectSelectedConnectionId, (state, id: GUID) => id],
  (connectionId, id): boolean => (connectionId ? connectionId === id : false),
);

/**
 * @param state current app state
 * @param id The ID of the scan entity to check whether it is a part of selected connection.
 * @returns true if given id is part of selected connection
 */
export const selectIsScanInSelectedConnection = createSelector(
  [selectSelectedConnectionDetails, (state, id: GUID) => id],
  (connectionDetails, id): boolean => {
    if (connectionDetails) {
      return (
        connectionDetails.sourceId === id || connectionDetails.targetId === id
      );
    }
    return false;
  },
);

/**
 *
 * @returns all point cloud streams for the hovered entities.
 * @param entityIds Array of entity IDs for which to retrieve point cloud stream IDs.
 * @param scanEntities Array of scan entities for which to retrieve point cloud stream IDs.
 * @param state current app state
 */
export const selectPointCloudStreamIdsForEntities = curryAppSelector(
  createSelector(
    [
      (state: RootState, scanEntities: RevisionScanEntity[]) => scanEntities,
      selectLoadedIElements,
    ],
    (scanEntities, iElements): GUID[] =>
      getPointCloudStreamIdsForEntities(
        scanEntities,
        iElements.filter(isIElementGenericPointCloudStream),
      ),
  ),
);

/**
 * @param state current application state
 * @param ids the entity ids to compute the recursive entities for
 * @returns all entities that are impacted directly or indirectly with the current entities in the array.
 */
export const selectEntitiesRecursive = curryAppSelector(
  createSelector(
    [
      (state: RootState, ids: GUID[]) => selectAffectedAncestorIds(ids)(state),
      selectRevisionEntityChildrenMap,
      selectRevisionEntityMap,
    ],
    computeRecursiveEntities,
  ),
);

/**
 * @returns all entities that are selected directly or indirectly (through selected clusters).
 */
export const selectSelectedEntitiesRecursive = createSelector(
  [
    selectEntitiesToEdit,
    selectRevisionEntityChildrenMap,
    selectRevisionEntityMap,
  ],
  computeRecursiveEntities,
);

/**
 * @returns the IDs of all entities that are selected directly or indirectly (through selected clusters).
 */
export const selectSelectedEntityIdsRecursive = createSelector(
  [selectSelectedEntitiesRecursive],
  (selectedEntities): GUID[] => selectedEntities.map((entity) => entity.id),
);

/**
 * @returns the IDs whose visibility is changed for the selected entities.
 * Includes the root when all non-deleted entities below it are selected.
 */
export const selectSelectedEntityIdsForVisibility = createSelector(
  [
    selectSelectedEntityIdsRecursive,
    selectRevisionEntityChildrenMap,
    selectRootEntity,
  ],
  (selectedEntityIds, childrenMap, rootEntity): GUID[] => {
    if (!rootEntity) return selectedEntityIds;

    const selectedIds = new Set(selectedEntityIds);
    const allDescendantIds = getDescendantIds(childrenMap, rootEntity.id).slice(
      1,
    );

    if (
      allDescendantIds.length > 0 &&
      !selectedIds.has(rootEntity.id) &&
      allDescendantIds.every((id) => selectedIds.has(id))
    ) {
      return [...selectedEntityIds, rootEntity.id];
    }

    return selectedEntityIds;
  },
);

/**
 * @returns all scans that are selected directly or indirectly (through selected clusters).
 *  This will be exactly the scans which are also manipulated when editing.
 */
export const selectSelectedScansRecursive = createSelector(
  [selectSelectedEntitiesRecursive],
  (selectedEntities): RevisionScanEntity[] =>
    selectedEntities.filter(isRevisionScanEntity),
);

/**
 * @returns all point cloud streams for the selected entities,
 * including those selected directly or indirectly (through selected clusters).
 */
export const selectPointCloudStreamIdsForSelectedEntities = createSelector(
  [
    selectSelectedScansRecursive,
    selectSelectedConnectionDetails,
    selectRevisionEntityMap,
    selectLoadedIElements,
  ],
  (
    selectedScansRecursive,
    selectedConnectionDetails,
    entityMap,
    iElements,
  ): GUID[] => {
    if (selectedScansRecursive.length === 0 && !selectedConnectionDetails) {
      return EMPTY_ARRAY;
    }

    const allSelectedScanIds: GUID[] = selectedScansRecursive.map(
      (scan) => scan.id,
    );

    if (selectedConnectionDetails) {
      allSelectedScanIds.push(selectedConnectionDetails.sourceId);
      allSelectedScanIds.push(selectedConnectionDetails.targetId);
    }

    const scanEntities = allSelectedScanIds
      .map((id) => entityMap[id])
      .filter((entity): entity is RevisionScanEntity => !!entity);

    return [
      ...new Set(
        getPointCloudStreamIdsForEntities(
          scanEntities,
          iElements.filter(isIElementGenericPointCloudStream),
        ),
      ),
    ];
  },
);

/**
 * @returns `true` if ALL of the selected scans are visible.
 *  `false` if any of the selected scans are hidden (directly or indirectly).
 */
export const selectAreSelectedScansVisible = createSelector(
  [selectRevisionEntityMap, selectSelectedScansRecursive, selectVisibilityMap],
  (entityMap, selectedScans, visibilityMap): boolean =>
    selectedScans.every((scan) =>
      // All ancestors must be visible for the scan to be visible
      getAncestorIds(entityMap, scan.id).every(
        (id) => visibilityMap[id] !== false,
      ),
    ),
);

/**
 * @description is a memoized selector that derives the ids of scans returned by selectSelectedScansRecursive selector
 * @returns all scan ids that are selected directly or indirectly (through selected clusters).
 */
export const selectSelectedScanIdsRecursive = createSelector(
  [selectSelectedScansRecursive],
  (selectedScans): GUID[] => selectedScans.map((scan) => scan.id),
);

/**
 * @returns all captures that are selected directly or indirectly (through selected clusters).
 *  Mostly analogue to the selected scans, but with special handling for SiteScape:
 *  A SiteScape cluster will count as a capture, while the contained individual scans do not.
 */
export const selectSelectedCapturesRecursive = createSelector(
  [selectSelectedEntitiesRecursive, selectRevisionAtomicClusterIds],
  computeActionableCaptures,
);

/**
 * @param state current application state
 * @param entities the entities to compute the affected captures for
 * @returns all captures that are affected directly or indirectly for the given set of entities.
 *  there is a special handling for SiteScape:
 *  A SiteScape cluster will count as a capture, while the contained individual scans do not.
 */
export const selectRelevantCaptures = curryAppSelector(
  createSelector(
    [
      (
        state: RootState,
        entities: CaptureTreeEntityRevision[],
      ): CaptureTreeEntityRevision[] => entities,
      selectRevisionAtomicClusterIds,
    ],
    computeActionableCaptures,
  ),
);

/**
 * @returns all clusters that are selected directly or indirectly (through other selected clusters).
 *  Special handling for SiteScape: SiteScape clusters are not included, because they are counted as capture.
 */
export const selectSelectedClustersRecursive = createSelector(
  [selectSelectedEntitiesRecursive, selectRevisionAtomicClusterIds],
  computeActionableClusters,
);

/**
 * @param state current application state
 * @param entities the entities to compute the affected clusters for
 * @returns all clusters that are impacted directly or indirectly by the entity.
 *  Special handling for SiteScape: SiteScape clusters are not included, because they are counted as capture.
 */
export const selectRelevantClusters = curryAppSelector(
  createSelector(
    [
      (state: RootState, entities: CaptureTreeEntityRevision[]) => entities,
      selectRevisionAtomicClusterIds,
    ],
    computeActionableClusters,
  ),
);

/**
 * @returns true if the given entity is selected (either directly, or through cluster or a connection)
 * @param state current app state
 * @param id the id of the scan to check
 */
export const selectIsScanSelected = curryAppSelector(
  createSelector(
    [
      selectSelectedScanIdsRecursive,
      (state: RootState, id: GUID) =>
        selectIsScanInSelectedConnection(state, id),
      (state, id: GUID) => id,
    ],
    (selectedEntityIds, isScanInSelectedConnection, scanId): boolean => {
      if (!selectedEntityIds.length && !isScanInSelectedConnection) {
        return false;
      }
      return selectedEntityIds.includes(scanId) || isScanInSelectedConnection;
    },
  ),
);

/**
 * @param state current app state
 * @returns The ID of the entity that is currently being hovered or `undefined` otherwise.
 */
export function selectHoveredEntityId(state: RootState): GUID | undefined {
  return state.dataPreparationUi.hoveredEntityId;
}

/**
 * @param state current app state
 * @returns The id of hovered connection.
 */
export function selectHoveredConnectionId(state: RootState): GUID | undefined {
  return state.dataPreparationUi.hoveredConnectionId;
}

/**
 * @returns The registration revision of the hovered connection.
 */
export const selectHoveredConnectionDetails = createSelector(
  [selectHoveredConnectionId, selectRevisionRegistrationEdges],
  (hoveredConnectionId, edges): RegistrationEdgeRevision | undefined =>
    edges.find((edge) => edge.id === hoveredConnectionId),
);

/**
 * @param state current app state
 * @param id The ID of the connection to check whether it is hovered.
 * @returns true if given id is same as hovered connection id
 */
export const selectIsConnectionHovered = curryAppSelector(
  createSelector(
    [selectHoveredConnectionId, (state, id: GUID) => id],
    (connectionId, id): boolean => (connectionId ? connectionId === id : false),
  ),
);

/**
 * @param state current app state
 * @param id The ID of the scan entity to check whether it is a part of hovered connection.
 * @returns true if given id is part of hovered connection
 */
export const selectIsScanInHoveredConnection = createSelector(
  [selectHoveredConnectionDetails, (state, id: GUID) => id],
  (connectionDetails, id): boolean => {
    if (connectionDetails) {
      return (
        connectionDetails.sourceId === id || connectionDetails.targetId === id
      );
    }
    return false;
  },
);

/**
 * @param state current app state
 * @returns true if any entity is highlighted (either selected or hovered)
 */
export const selectIsAnyEntityHighlighted = createSelector(
  [
    selectHoveredEntityId,
    selectHoveredConnectionId,
    selectSelectedScanIdsRecursive,
  ],
  (hoveredEntityId, hoveredConnectionId, selectedScanIds): boolean =>
    selectedScanIds.length > 0 ||
    hoveredEntityId !== undefined ||
    hoveredConnectionId !== undefined,
);

/**
 * @returns all entity ids that are indirectly hovered (e.g when we hover on a cluster).
 */
export const selectHoveredEntityIdsRecursive = createSelector(
  [selectHoveredEntityId, selectRevisionEntityChildrenMap],
  (entityId, childrenMap): GUID[] | undefined => {
    if (entityId !== undefined) {
      return getDescendantIds(childrenMap, entityId);
    }
  },
);

/**
 * @returns the IDs of all scans that are highlighted.
 *  This includes the selected and hovered scans, either directly or through clusters or connections.
 *  If no scans are selected or hovered, all scans are highlighted.
 */
export const selectHighlightedScanIds = createSelector(
  [
    selectRevisionScans,
    selectSelectedScanIdsRecursive,
    selectHoveredEntityIdsRecursive,
    selectHoveredConnectionDetails,
  ],
  (
    scanEntities,
    selectedScanIds,
    hoveredEntityIds,
    hoveredConnectionDetails,
  ): GUID[] => {
    const highlightedScanIds = new Set<GUID>(selectedScanIds);

    hoveredEntityIds?.forEach((id) => {
      // Only add scan entities
      if (scanEntities.some((scan) => scan.id === id)) {
        highlightedScanIds.add(id);
      }
    });

    if (hoveredConnectionDetails) {
      highlightedScanIds.add(hoveredConnectionDetails.sourceId);
      highlightedScanIds.add(hoveredConnectionDetails.targetId);
    }

    // If no scans are selected or hovered, highlight all scans
    if (highlightedScanIds.size === 0) {
      return scanEntities.map((scan) => scan.id);
    }

    return [...highlightedScanIds];
  },
);

/**
 * @returns true if the given entity is being indirectly hovered (e.g. entity is a part of hovering cluster)
 * @param state current app state
 * @param entityId the entity to check
 */
export const selectIsEntityIndirectlyHovered = curryAppSelector(
  createSelector(
    [selectHoveredEntityIdsRecursive, (state, entityId?: GUID) => entityId],
    (hoveredEntityIds, entityId): boolean => {
      if (!!entityId && !!hoveredEntityIds) {
        return hoveredEntityIds.includes(entityId);
      }
      return false;
    },
  ),
);

/**
 * @returns true if the given scan is hovered (either directly or through a cluster or connection)
 * @param state current app state
 * @param id the id of scan to check
 */
export const selectIsScanHovered = curryAppSelector(
  createSelector(
    [
      (state: RootState, id: GUID) =>
        selectIsEntityIndirectlyHovered(id)(state),
      (state: RootState, id: GUID) =>
        selectIsScanInHoveredConnection(state, id),
    ],
    (isScanHovered, isScanInHoveredConnection): boolean =>
      isScanHovered || isScanInHoveredConnection,
  ),
);

/**
 * @returns true if the given scan should be highlighted/colored (either selected or hovered)
 * @param state current app state
 * @param id the id of scan to check
 */
export const selectIsScanHighlighted = curryAppSelector(
  createSelector(
    [
      selectIsAnyEntityHighlighted,
      (state, id: GUID) => selectIsScanSelected(id)(state),
      (state, id: GUID) => selectIsScanHovered(id)(state),
    ],
    (selectIsAnyEntityHighlighted, isScanSelected, isScanHovered): boolean =>
      !selectIsAnyEntityHighlighted || isScanSelected || isScanHovered,
  ),
);

/**
 * @returns true if the given connection should be highlighted/colored when quality color coding is enabled
 * @param state current app state
 * @param id the id of connection to check
 */
export const selectIsConnectionHighlighted = curryAppSelector(
  createSelector(
    [
      selectHoveredConnectionId,
      selectSelectedConnectionId,
      (state, id: GUID) => selectIsConnectionHovered(id)(state),
      (state, id: GUID) => selectIsConnectionSelected(state, id),
    ],
    (
      hoveredConnectionId,
      selectedConnectionId,
      isConnectionHovered,
      isConnectionSelected,
    ): boolean => {
      const noConnectionHovered = hoveredConnectionId === undefined;

      const noConnectionSelected = selectedConnectionId === undefined;

      if (
        (noConnectionHovered && noConnectionSelected) ||
        isConnectionHovered ||
        isConnectionSelected
      ) {
        return true;
      }
      return false;
    },
  ),
);

/**
 * @returns all point cloud streams for the hovered entities.
 * @param state current app state
 */
export const selectPointCloudStreamIdsForHoveredEntities = createSelector(
  [
    selectHoveredEntityIdsRecursive,
    selectHoveredConnectionDetails,
    selectRevisionEntityMap,
    selectLoadedIElements,
  ],
  (
    hoveredEntityIds,
    hoveredConnectionDetails,
    entityMap,
    iElements,
  ): GUID[] => {
    // Collect all relevant IDs: hovered entities + hovered connection endpoints
    const ids: GUID[] = hoveredEntityIds ? [...hoveredEntityIds] : [];
    if (hoveredConnectionDetails) {
      ids.push(
        hoveredConnectionDetails.sourceId,
        hoveredConnectionDetails.targetId,
      );
    }

    // Get all scan entities for those IDs
    const scanEntities = ids
      .map((id) => entityMap[id])
      .filter(
        (entity): entity is RevisionScanEntity =>
          !!entity && isRevisionScanEntity(entity),
      );

    // Get point cloud stream IDs for those scan entities
    return [
      ...new Set(
        getPointCloudStreamIdsForEntities(
          scanEntities,
          iElements.filter(isIElementGenericPointCloudStream),
        ),
      ),
    ];
  },
);

/**
 * @param state The current application state.
 * @returns The map from entity IDs to their visibility.
 *  `undefined` means visible.
 */
export function selectVisibilityMap(state: RootState): VisibilityMap {
  return state.dataPreparationUi.visibilityMap;
}

/**
 * Finds all connected components in the revision graph using depth-first search.
 * Uses an adjacency map for efficient edge lookups.
 * Two entities are connected if they share a 'partOf' registration edge.
 *
 * @returns Array of groups, where each group is an array of entity IDs that are connected to each other.
 *  Groups are disjoint - each entity belongs to exactly one group.
 */
export const selectPartOfConnectedComponents = createSelector(
  [selectRevisionScans, selectPartOfEdges, selectRevisionAtomicClusterIds],
  (scanEntities, registrationEdges, atomicClusterIds) =>
    findConnectedComponents(scanEntities, registrationEdges, atomicClusterIds),
);

/**
 * Finds all connected components in the revision graph using depth-first search.
 * Uses an adjacency map for efficient edge lookups.
 * Two entities are connected if they share a 'global' or 'partOf' registration edge.
 *
 * @returns Array of groups, where each group is an array of entity IDs that are connected to each other.
 *  Groups are disjoint - each entity belongs to exactly one group.
 */
export const selectAllConnectedComponents = createSelector(
  [
    selectRevisionScans,
    selectGlobalEdges,
    selectPartOfEdges,
    selectRevisionAtomicClusterIds,
  ],
  (scanEntities, globalEdges, partOfEdges, atomicClusterIds) =>
    findConnectedComponents(
      scanEntities,
      [...globalEdges, ...partOfEdges],
      atomicClusterIds,
    ),
);

/**
 * Finds all connected components that are reported as having an issue
 * in the capture tree, including global and partOf edges.
 * Uses an adjacency map for efficient edge lookups.
 * Two entities are connected if they share a 'global' or 'partOf' registration edge.
 * The selector now takes separate edge selectors as inputs and merges them inside
 * the output selector, rather than combining them in the input array.
 *
 * @param state The current application state
 * @param entities The entities reported as having an issue to check for connections in.
 * @returns Array of groups, where each group is an array of entity IDs that are connected to each other.
 *  Groups are disjoint - each entity belongs to exactly one group.
 */
export const selectConnectedIssueComponents = curryAppSelector(
  createSelector(
    [
      (state: RootState, entities: CaptureTreeEntityRevision[]) => entities,
      selectGlobalEdges,
      selectPartOfEdges,
      selectRevisionAtomicClusterIds,
    ],
    (issueScans, globalEdges, partOfEdges, atomicClusterIds) =>
      findConnectedComponents(
        issueScans,
        [...globalEdges, ...partOfEdges],
        atomicClusterIds,
      ),
  ),
);

// These colors are temporary. Once the layered capture markers are implemented
// we might use the existing data-comparison color palette with opacity instead.
// Ticket reference: https://faro01.atlassian.net/browse/NRT-3726
export const fixedDataComparisonColors: Record<string, ColorString> = {
  cyan: "#4877D5",
  orange: "#C67448",
  pink: "#C04BB8",
  purple: "#885CD6",
  teal: "#43BFCF",
  lime: "#9BAF64",
  violet: "#753A99",
  brown: "#9F6356",
  burgundy: "#8F438B",
  deepGreen: "#4B877C",
  blue: "#386184",
};

export const fixedDataComparisonColorsDark: Record<string, ColorString> = {
  cyan: "#699DDE",
  orange: "#CE9073",
  pink: "#CE69C4",
  purple: "#9A6DDE",
  teal: "#63C6D5",
  lime: "#B4C679",
  violet: "#9369B8",
  brown: "#B77C6A",
  burgundy: "#AB60A5",
  deepGreen: "#59A999",
  blue: "#5A85AE",
};

const fixedDataComparisonColorsSorted: ColorString[] = [
  fixedDataComparisonColors.cyan,
  fixedDataComparisonColors.orange,
  fixedDataComparisonColors.pink,
  fixedDataComparisonColors.purple,
  fixedDataComparisonColors.teal,
  fixedDataComparisonColors.lime,
  fixedDataComparisonColors.brown,
  fixedDataComparisonColors.burgundy,
  fixedDataComparisonColors.violet,
  fixedDataComparisonColors.deepGreen,
  fixedDataComparisonColors.blue,
];

const fixedDataComparisonColorsSortedDark: ColorString[] = [
  fixedDataComparisonColorsDark.cyan,
  fixedDataComparisonColorsDark.orange,
  fixedDataComparisonColorsDark.pink,
  fixedDataComparisonColorsDark.purple,
  fixedDataComparisonColorsDark.teal,
  fixedDataComparisonColorsDark.lime,
  fixedDataComparisonColorsDark.brown,
  fixedDataComparisonColorsDark.burgundy,
  fixedDataComparisonColorsDark.violet,
  fixedDataComparisonColorsDark.deepGreen,
  fixedDataComparisonColorsDark.blue,
];

/**
 * @param state The current application state
 * @param isDarkMode Whether to return the colors for the dark theme
 * @returns The map of entity IDs to colors based on connectivity and fixed status.
 *  Uses all connected components (global and partOf edges) if byConnectivity coloring is selected,
 *  otherwise uses only partOf edges. Fixed scans use the fixed palette at the same group index.
 */
export const selectEntityIdToColorMap = curryAppSelector(
  createSelector(
    [
      selectAllConnectedComponents,
      selectPartOfConnectedComponents,
      selectScanColoring,
      selectRevisionEntityMap,
      (state: RootState, isDarkMode: boolean) => isDarkMode,
    ],
    (
      allConnectedGroups,
      partOfGroups,
      scanColoring,
      entityMap,
      isDarkMode,
    ): Record<GUID, ColorString> => {
      const entityIdToColorMap: Record<GUID, ColorString> = {};
      const groups =
        scanColoring === ScanColoring.byConnectivity
          ? allConnectedGroups
          : partOfGroups;
      const palette = isDarkMode
        ? dataComparisonColorsSortedDark
        : dataComparisonColorsSorted;
      const paletteFixedMarkers = isDarkMode
        ? fixedDataComparisonColorsSortedDark
        : fixedDataComparisonColorsSorted;
      // Use the same scan marker color when coloring by intensity or original color
      const hasUniformColor =
        scanColoring === ScanColoring.intensity ||
        scanColoring === ScanColoring.originalColor;
      const uniformColor = isDarkMode
        ? dataComparisonColorsDark.primary
        : blue[500];

      groups.forEach((group: GUID[], groupIndex: number) => {
        const groupColor = hasUniformColor
          ? uniformColor
          : colorForIndex(groupIndex, palette);
        const fixedGroupColor = hasUniformColor
          ? uniformColor
          : colorForIndex(groupIndex, paletteFixedMarkers);
        group.forEach((entityId: GUID) => {
          const entity = entityMap[entityId];
          entityIdToColorMap[entityId] =
            entity && isRevisionScanEntity(entity) && entity.isFixed === true
              ? fixedGroupColor
              : groupColor;
        });
      });

      return entityIdToColorMap;
    },
  ),
);

/**
 * @returns The map of IElement IDs to colors.
 * @param state The current application state.
 * @param isDarkMode Whether to return the colors for the dark theme
 */
export const selectIElementIdToColorMap = curryAppSelector(
  createSelector(
    [
      (state: RootState, isDarkMode: boolean) =>
        selectEntityIdToColorMap(isDarkMode)(state),
      selectLoadedIElements,
    ],
    (entityIdToColorMap, iElements) => {
      const iElementIdToColorMap: Record<GUID, ColorString> = {};
      const scanIds: GUID[] = Object.keys(entityIdToColorMap);

      scanIds.forEach((scanId) => {
        const pointCloudStream = getPointCloudStreamForScanEntity(
          iElements.filter(isIElementGenericPointCloudStream),
          scanId,
        );
        if (pointCloudStream?.id) {
          iElementIdToColorMap[pointCloudStream.id] =
            entityIdToColorMap[scanId];
        }
      });

      return iElementIdToColorMap;
    },
  ),
);

/**
 * @param state The current application state.
 * @returns All revision scans that are valid (not marked as deleted).
 */
export const selectValidRevisionScans = createSelector(
  [selectRevisionEntities],
  (revisionEntities): RevisionScanEntity[] =>
    revisionEntities
      .filter(isRevisionScanEntity)
      .filter((scan) => !isEntityDeletedOnRevision(scan)),
);

/**
 * @param state The current application state.
 * @returns All revision scans that are invalid (marked as deleted).
 */
export const selectInvalidRevisionScans = createSelector(
  [selectRevisionEntities],
  (revisionEntities): RevisionScanEntity[] =>
    revisionEntities
      .filter(isRevisionScanEntity)
      .filter((scan) => isEntityDeletedOnRevision(scan)),
);

/**
 * @param state The current application state.
 * @returns All entities which are valid, i.e. should be displayed to the user and remain in the revision.
 */
export const selectValidRevisionEntities = createSelector(
  [selectRevisionEntities],
  (revisionEntities): CaptureTreeEntityRevision[] =>
    revisionEntities.filter((entity) => !isEntityDeletedOnRevision(entity)),
);

/**
 * @param _state The current application state.
 * @param entityId Id of the revision entity to get the cached world transform for.
 * @returns The cached world transform of the entity.
 */
export const selectRevisionEntityWorldTransformCache = curryAppSelector(
  createSelector(
    [
      selectDataPreparationTransformCache,
      (_state: RootState, entityId?: GUID) => entityId,
    ],
    getEntityTransformCache,
  ),
);

/**
 * @param _state The current application state.
 * @param entityIds The IDs of the entities to get the world positions for.
 * @returns The world positions of the entities with the given IDs.
 */
export const selectRevisionEntityWorldPositions = curryAppSelector(
  createSelector(
    [
      selectDataPreparationTransformCache,
      (_state: RootState, entityIds: GUID[]) => entityIds,
    ],
    (transformCache, entityIds) =>
      entityIds.map((id) => getEntityPosition(transformCache, id)),
  ),
);
/**
 * @param state The current application state.
 * @param entityId The ID of the entity to get the scan center for.
 * @returns The scan center of the entity.
 *  - If the entity is a scan, the center is the position of the scan.
 *  - If the entity is a cluster, the center is the average position of all descendant scans.
 *  - If cluster is empty, the position of the cluster itself is used.
 */

export const selectRevisionEntityScanCenter = curryAppSelector(
  createSelector(
    [
      selectDataPreparationTransformCache,
      (state, entityId?: GUID) =>
        selectRevisionEntityDescendantScans(entityId)(state),
      (state, entityId?: GUID) => entityId,
    ],
    (transformCache, descendantScans, entityId) => {
      if (!descendantScans.length) {
        return getEntityPosition(transformCache, entityId);
      }

      const center = new Vector3();

      for (const scan of descendantScans) {
        center.add(getEntityPosition(transformCache, scan.id));
      }

      return center.divideScalar(descendantScans.length);
    },
  ),
);
